import { useRef, useState } from 'react';
import { MAX_MASJID_COLOURS } from '@noorview/core';
import { Icon } from './icons';
import { ColourButton } from './pickers';
import { coloursFromImage } from '../app/palette';
import { toast } from '../data/store';

/**
 * The masjid's own colours: added by hex code or the colour picker, or found in the logo or any image. They're
 * offered first in every colour choice in the Studio (never chosen for the user). Up to 8.
 */
export function MasjidColoursEditor({ value, onChange, logo, disabled }: { value: readonly string[]; onChange: (v: string[]) => void; logo?: string | null; disabled?: boolean }) {
  const [hex, setHex] = useState('');
  const [pick, setPick] = useState('#0A84FF');
  const [busy, setBusy] = useState(false);
  const file = useRef<HTMLInputElement>(null);
  const full = value.length >= MAX_MASJID_COLOURS;
  const next = hex.length === 6 ? `#${hex.toUpperCase()}` : pick.toUpperCase();
  const add = () => {
    if (full) return;
    if (value.includes(next)) { toast(`${next} is already one of your colours`); return; }
    onChange([...value, next]); setHex('');
  };
  const detect = async (src: string | Blob, from: string) => {
    setBusy(true);
    try {
      const found = await coloursFromImage(src, MAX_MASJID_COLOURS);
      if (!found.length) { toast(`No colours found in ${from}. Try another image, or add them by hex code.`, { bad: true }); return; }
      onChange(found);
      toast(`Found ${found.length} colour${found.length === 1 ? '' : 's'} in ${from}. Remove any you don’t want.`);
    } finally { setBusy(false); }
  };
  return (
    <div className="mc">
      {value.length > 0 ? (
        <ul className="mc-list" aria-label="Masjid colours">
          {value.map((c) => (
            <li key={c} className="mc-chip">
              <span className="mc-dot" style={{ background: c }} aria-hidden />
              <span className="mono">{c}</span>
              <button type="button" className="mc-x" disabled={disabled} aria-label={`Remove ${c}`} title={`Remove ${c}`} onClick={() => onChange(value.filter((x) => x !== c))}><Icon name="x" size={12} stroke={2.6} /></button>
            </li>
          ))}
        </ul>
      ) : null}
      <div className="mc-add">
        <label className="hex">
          <span className="h">#</span>
          <input className="bare mono" aria-label="New colour, hex code" maxLength={6} placeholder={pick.slice(1)} value={hex} disabled={disabled || full}
            onChange={(e) => setHex(e.target.value.replace(/[^0-9a-f]/gi, '').slice(0, 6))} onKeyDown={(e) => { if (e.key === 'Enter') add(); }} />
          <ColourButton label="Pick a colour" value={next} onChange={(h) => { setPick(h); setHex(''); }} />
        </label>
        <button type="button" className="btn sm secondary" disabled={disabled || full || (hex.length > 0 && hex.length < 6)} onClick={add} title={full ? `Up to ${MAX_MASJID_COLOURS} colours` : undefined}><Icon name="plus" size={16} />Add</button>
      </div>
      <input ref={file} type="file" accept="image/png,image/jpeg,image/webp,image/gif" hidden onChange={(e) => { const f = e.target.files?.[0]; if (f) void detect(f, 'the image'); e.target.value = ''; }} />
      <div className="mc-find">
        {logo && <button type="button" className="btn sm plain" disabled={disabled || busy} onClick={() => void detect(logo, 'your logo')}><Icon name="sparkles" size={16} />From logo</button>}
        <button type="button" className="btn sm plain" disabled={disabled || busy} onClick={() => file.current?.click()}><Icon name="image" size={16} />From an image</button>
      </div>
    </div>
  );
}
